/* eslint-disable react/prop-types */
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MENU_TEXT_FONTS,
  normalizeMenuTextFont,
} from "../../../constants/menuTextFonts";

const FONT_LABELS = {
  roboto: "Roboto",
  inter: "Inter",
  poppins: "Poppins",
  montserrat: "Montserrat",
  lato: "Lato",
  nunito: "Nunito",
  openSans: "Open Sans",
  playfair: "Playfair Display",
  merriweather: "Merriweather",
  oswald: "Oswald",
  raleway: "Raleway",
  ubuntu: "Ubuntu",
  rubik: "Rubik",
  sourceSansPro: "Source Sans Pro",
  workSans: "Work Sans",
  bitter: "Bitter",
  aachen: "Aachen",
  americanType: "American Type",
  avantGarde: "Avant Garde",
  arial: "Arial",
  arialBlack: "Arial Black",
  bankGothic: "Bank Gothic",
  bauhaus: "Bauhaus",
  bostonTraffic: "Boston Traffic",
  brody: "Brody",
  brushScript: "Brush Script",
  centurySchoolbook: "Century Schoolbook",
  comicSans: "Comic Sans",
  cooper: "Cooper",
  cooperplate: "Cooperplate",
  courierNew: "Courier New",
  edwardianScript: "Edwardian Script",
  futura: "Futura",
  garamond: "Garamond",
  helvetica: "Helvetica",
  impact: "Impact",
  myriadPro: "Myriad Pro",
  murrayHill: "Murray Hill",
  oldEnglish: "Old English",
  papyrus: "Papyrus",
  plaza: "Plaza",
  serpentine: "Serpentine",
  systems: "Systems",
  stencil: "Stencil",
  topSecret: "Top Secret",
  trajan: "Trajan",
  timesNewRoman: "Times New Roman",
  timesBold: "Times Bold",
  unitedStates: "United States",
  yearbook: "Yearbook",
};

const FONT_STACKS = {
  roboto: "Roboto, Arial, sans-serif",
  inter: "Inter, Arial, sans-serif",
  poppins: "Poppins, Arial, sans-serif",
  montserrat: "Montserrat, Arial, sans-serif",
  lato: "Lato, Arial, sans-serif",
  nunito: "Nunito, Arial, sans-serif",
  openSans: "'Open Sans', Arial, sans-serif",
  playfair: "'Playfair Display', Georgia, serif",
  merriweather: "Merriweather, Georgia, serif",
  oswald: "Oswald, Arial, sans-serif",
  raleway: "Raleway, Arial, sans-serif",
  ubuntu: "Ubuntu, Arial, sans-serif",
  rubik: "Rubik, Arial, sans-serif",
  sourceSansPro: "'Source Sans 3', Arial, sans-serif",
  workSans: "'Work Sans', Arial, sans-serif",
  bitter: "Bitter, Georgia, serif",
  aachen: "Aachen, 'Arial Black', serif",
  americanType: "'American Typewriter', serif",
  avantGarde: "'Avant Garde', 'Century Gothic', sans-serif",
  arial: "Arial, Helvetica, sans-serif",
  arialBlack: "'Arial Black', Arial, sans-serif",
  bankGothic: "'Bank Gothic', 'Arial Black', sans-serif",
  bauhaus: "'Bauhaus 93', 'Century Gothic', sans-serif",
  bostonTraffic: "'Boston Traffic', Impact, sans-serif",
  brody: "Brody, 'Brush Script MT', cursive",
  brushScript: "'Brush Script MT', 'Segoe Script', cursive",
  centurySchoolbook: "'Century Schoolbook', Georgia, serif",
  comicSans: "'Comic Sans MS', 'Comic Sans', cursive",
  cooper: "Cooper, 'Cooper Black', serif",
  cooperplate: "'Copperplate', 'Copperplate Gothic Light', serif",
  courierNew: "'Courier New', Courier, monospace",
  edwardianScript: "'Edwardian Script ITC', 'Brush Script MT', cursive",
  futura: "Futura, 'Trebuchet MS', sans-serif",
  garamond: "Garamond, Georgia, serif",
  helvetica: "Helvetica, Arial, sans-serif",
  impact: "Impact, Haettenschweiler, 'Arial Black', sans-serif",
  myriadPro: "'Myriad Pro', 'Segoe UI', Arial, sans-serif",
  murrayHill: "'Murray Hill', 'Brush Script MT', cursive",
  oldEnglish: "'Old English Text MT', 'Times New Roman', serif",
  papyrus: "Papyrus, fantasy",
  plaza: "Plaza, 'Trebuchet MS', sans-serif",
  serpentine: "Serpentine, 'Arial Black', sans-serif",
  systems: "Systems, 'Lucida Sans Unicode', sans-serif",
  stencil: "Stencil, 'Stencil Std', 'Arial Black', sans-serif",
  topSecret: "'Top Secret', Stencil, 'Arial Black', sans-serif",
  trajan: "Trajan, 'Times New Roman', serif",
  timesNewRoman: "'Times New Roman', Times, serif",
  timesBold: "'Times New Roman', Times, serif",
  unitedStates: "'United States', Stencil, 'Arial Black', sans-serif",
  yearbook: "Yearbook, 'Bookman Old Style', serif",
};

function FontSelect({ id, label, value, onChange }) {
  const normalized = normalizeMenuTextFont(value);
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const rootRef = useRef(null);
  const listId = useId();

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next && rootRef.current) {
        const rect = rootRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        setOpenUp(spaceBelow < 180);
      }
      return next;
    });
  };

  return (
    <div className="menu-font-picker-select-wrap mb-3">
      <label htmlFor={id} className="menu-form-field-label">
        {label}
      </label>
      <div className="menu-font-picker-select-wrap__control" ref={rootRef}>
        <button
          id={id}
          type="button"
          className={`menu-font-picker-trigger${open ? " menu-font-picker-trigger--open" : ""}`}
          style={{ fontFamily: FONT_STACKS[normalized] }}
          aria-label={label}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          onClick={toggleOpen}
        >
          <span className="menu-font-picker-trigger__value">
            {FONT_LABELS[normalized] || normalized}
          </span>
          <span className="menu-font-picker-trigger__chevron" aria-hidden="true" />
        </button>

        {open ? (
          <ul
            id={listId}
            className={`menu-font-picker-dropdown${openUp ? " menu-font-picker-dropdown--up" : ""}`}
            role="listbox"
            aria-label={label}
          >
            {MENU_TEXT_FONTS.map((font) => {
              const selected = normalized === font;
              return (
                <li key={font} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`menu-font-picker-dropdown__item${
                      selected ? " menu-font-picker-dropdown__item--active" : ""
                    }`}
                    style={{ fontFamily: FONT_STACKS[font] }}
                    onClick={() => {
                      onChange(font);
                      setOpen(false);
                    }}
                  >
                    {FONT_LABELS[font]}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

export default function MenuTextFontPicker({
  productTextFont,
  categoryTextFont,
  subCategoryTextFont,
  onProductTextFontChange,
  onCategoryTextFontChange,
  onSubCategoryTextFontChange,
  idPrefix = "menu-text-fonts",
  compact = false,
}) {
  const { t } = useTranslation();

  return (
    <div className="menu-text-font-picker">
      {!compact ? (
        <>
          <h3 className="menu-form-shell__title mb-1">{t("menuTextFontsTitle")}</h3>
          <p className="menu-form-shell__subtitle mb-3">
            Choose one font for products, categories and subcategories.
          </p>
        </>
      ) : (
        <h3 className="menu-form-shell__title mb-2">{t("menuTextFontsTitle")}</h3>
      )}

      <div className={compact ? "menu-form-display-fonts-grid" : undefined}>
        <FontSelect
          id={`${idPrefix}-product-text-font`}
          label={t("menuTextFontsProduct")}
          value={productTextFont}
          onChange={onProductTextFontChange}
        />
        <FontSelect
          id={`${idPrefix}-category-text-font`}
          label={t("menuTextFontsCategory")}
          value={categoryTextFont}
          onChange={onCategoryTextFontChange}
        />
        <FontSelect
          id={`${idPrefix}-subcategory-text-font`}
          label={t("menuTextFontsSubcategory")}
          value={subCategoryTextFont}
          onChange={onSubCategoryTextFontChange}
        />
      </div>
    </div>
  );
}
